import {
  DEFAULT_TOKEN_TIMEOUT_MINUTES,
  MILLISECONDS_PER_MINUTE,
  FlowBaseUrlEnum,
  WindowSizeClassification,
  CULTURE_ROUTE_PARAM,
  DeviceHeightClassification,
} from '@constants/app.constants';
import { ErrorType } from '@models/app.models';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { createSelector } from '@ngrx/store';
import { Duration } from 'luxon';
import {selectAppFeatureState} from '../reducers';
import {
  selectAnyJourneyCanSellStatuses,
  selectBooking,
  selectConfirmationNumber,
} from './booking.selector';
import { selectRouteParam, selectUrl } from './router.selector';
import { selectStationsState, selectStationsWithConnectionsState } from './station.selector';
import { selectAvailabilityTripTypeState, selectSearchInputCriteriaState, selectSearchInputState } from './availability.selector';
import { selectSelectedCurrencyCode, selectGeolocatedCountryCode } from './culture.selector';
import { SearchDataModel } from '@models/search.model';

/* istanbul ignore next  */
export const selectErrors = createSelector(
  selectAppFeatureState,
  (state) => state.errors,
);

/* istanbul ignore next  */
export const selectApiErrors = createSelector(
  selectAppFeatureState,
  (state) => state.apiErrors,
);

/* istanbul ignore next  */
export const selectFormError = createSelector(
  selectAppFeatureState,
  (state) => state.formError,
);

/* istanbul ignore next  */
export const selectAppAutoSearch = createSelector(
  selectAppFeatureState,
  (state) => state.autoSearch,
);

/* istanbul ignore next  */
export const selectAppAutoSell = createSelector(
  selectAppFeatureState,
  (state) => state.autoSell,
);

/* istanbul ignore next  */
export const selectPathSteps = createSelector(
  selectAppFeatureState,
  (state) => state.pathSteps,
);

export const selectIsFlightStepCompleted = createSelector(
  selectAppFeatureState,
  (state) =>
    state.pathSteps
    .find((s) => s.name === FlowPathEnum.FLIGHTS)?.completed ?? false,
);

export const selectValidPathSteps = createSelector(
  selectPathSteps,
  selectAnyJourneyCanSellStatuses,
  (steps, journeyStatuses) =>
    steps.filter((s) => {
      switch (s.name) {
        case FlowPathEnum.BAGS:
          return !!journeyStatuses?.canSellBags;
        case FlowPathEnum.SEATS:
          return !!journeyStatuses?.canSellSeats;
        case FlowPathEnum.MEALS:
          return !!journeyStatuses?.canSellMeals;
        case FlowPathEnum.EXTRAS:
          return !!journeyStatuses?.canSellExtras;
        default:
          return true;
      }
    }),
);

export const selectPathStepsActive = createSelector(
  selectValidPathSteps,
  (pathSteps) => pathSteps.find((s) => s.active),
);

export const selectPathStepsPrevious = createSelector(
  selectValidPathSteps,
  (pathSteps) => pathSteps[pathSteps.findIndex((s) => s.active) - 1],
);

export const selectPathStepsNext = createSelector(
  selectValidPathSteps,
  (pathSteps) => pathSteps[pathSteps.findIndex((s) => s.active) + 1],
);

export const selectIsTokenProvenValid = createSelector(
  selectAppFeatureState,
  (state) => state.isTokenProvenValid,
);

export const selectPaymentError = createSelector(selectErrors, (errors) =>
  errors.find((err) => err.type === ErrorType.SubmitPaymentFailure),
);

export const selectBookingCompleteError = createSelector(
  selectErrors,
  (errors) =>
    errors.find((err) => err.type === ErrorType.BookingCompleteFailure),
);

export const selectPaymentsDeclinedError = createSelector(
  selectErrors,
  (errors) =>
    errors.find((err) => err.type === ErrorType.BookingPaymentsDeclined),
);

export const selectTokenRefreshedAt = createSelector(
  selectAppFeatureState,
  (state) => state.tokenRefreshedAt,
);

export const selectTokenRefreshedAtOrNow = createSelector(
  selectTokenRefreshedAt,
  (tokenRefreshedAt) => tokenRefreshedAt ?? new Date(),
);

export const selectTokenExpirationMinutes = createSelector(
  selectAppFeatureState,
  (state) => state.tokenTimeoutInMinutes,
);

export const selectTokenAutRefreshCount = createSelector(
  selectAppFeatureState,
  (state) => state.tokenAutoRefreshCount,
);

export const selectTokenExpirationMinutesOrDefault = createSelector(
  selectTokenExpirationMinutes,
  (tokenTimeoutInMinutes) =>
    tokenTimeoutInMinutes ?? DEFAULT_TOKEN_TIMEOUT_MINUTES,
);

export const selectTokenExpiresAt = createSelector(
  selectTokenRefreshedAtOrNow,
  selectTokenExpirationMinutesOrDefault,
  (tokenRefreshedAt, tokenTimeoutInMinutes) => {
    return new Date(
      tokenRefreshedAt.getTime() +
        tokenTimeoutInMinutes * MILLISECONDS_PER_MINUTE,
    );
  },
);

export const selectTokenTimeRemaining = createSelector(
  selectTokenExpiresAt,
  (tokenExpiresAt) =>
    Duration.fromMillis(tokenExpiresAt.getTime() - Date.now()),
);
//TODO PENDING REQUESTS REFACTOR
export const selectDoShowLoader = createSelector(
  selectAppFeatureState,
  (state) => state.doShowLoader,
);

export const selectPnrExists = createSelector(
  selectConfirmationNumber,
  (confirmationNumber) => !!confirmationNumber,
);

export const selectIsModifyAndPnrExists = createSelector(
  selectUrl,
  selectBooking,
  (url, booking) =>
    url?.includes(`/${FlowBaseUrlEnum.MODIFY}/`) && !!booking?.recordLocator,
);

export const selectShowModifySearchWidget = createSelector(
  selectAppFeatureState,
  (state) => state.showModifySearchWidget,
);

export const selectShowModifySearchNavbarWidget = createSelector(
  selectAppFeatureState,
  (state) => state.showModifySearchNavbarWidget,
);

/* istanbul ignore next  */
export const selectShouldDeferUiUpdates = createSelector(
  selectAppFeatureState,
  (state) => state.shouldDeferUiUpdates,
);

export const selectIsIos = createSelector(selectAppFeatureState, (state) => {
  return state.platforms.includes('ios');
});

export const selectIsAndroid = createSelector(
  selectAppFeatureState,
  (state) => {
    return state.platforms.includes('android');
  },
);

export const selectIsMobile = createSelector(
  selectIsAndroid,
  selectIsIos,
  (isAndroid, isIos) => {
    return isAndroid || isIos;
  },
);

export const selectIsMobileWeb = createSelector(
  selectAppFeatureState,
  (state) => {
    return state.platforms.includes('mobileweb');
  },
);

export const selectIsWeb = createSelector(
  selectIsMobile,
  selectIsMobileWeb,
  (isMobile, isMobileWeb) => {
    return !isMobile || isMobileWeb;
  },
);

export const selectShouldUseMobileStyling = createSelector(
  selectAppFeatureState,
  (state) => state.windowSizeClassification === WindowSizeClassification.Mobile || state.windowSizeClassification === WindowSizeClassification.SmallMobile,
);

export const selectShouldUseSmallMobileStyling = createSelector(
  selectAppFeatureState,
  (state) => state.windowSizeClassification === WindowSizeClassification.SmallMobile,
);

export const selectShouldUseTabletStyling = createSelector(
  selectAppFeatureState,
  (state) => state.windowSizeClassification === WindowSizeClassification.Tablet,
);

export const selectIsLargeDesktop = createSelector(
  selectAppFeatureState,
  (state) => state.windowSizeClassification === WindowSizeClassification.LargeDesktop,
);

export const selectIsMediumDesktop = createSelector(
  selectAppFeatureState,
  (state) => state.windowSizeClassification === WindowSizeClassification.MediumDesktop,
);

export const selectIsSmallDeviceHeight = createSelector(
  selectAppFeatureState,
  (state) => state.deviceHeightClassification === DeviceHeightClassification.Small,
);

export const selectShowTripSummary = createSelector(
  selectAppFeatureState,
  (state) => state.showTripSummary,
);

export const selectIsSkipToPaymentModalDisplayed = createSelector(
  selectAppFeatureState,
  (state) => state.skipToPaymentModalDisplayed,
);

export const selectSearchData = createSelector(
  selectStationsState,
  selectStationsWithConnectionsState,
  selectRouteParam(CULTURE_ROUTE_PARAM),
  selectAppAutoSearch,
  selectSearchInputState,
  selectAvailabilityTripTypeState,
  selectSearchInputCriteriaState,
  selectSelectedCurrencyCode,
  selectGeolocatedCountryCode,
  (
    stations,
    stationsWithConnections,
    culture,
    autoSearch,
    searchReq,
    tripType,
    lastSearchedCriteria,
    selectedCurrencyCode,
    countryCode,
  ) => {
    return {
      stations,
      stationsWithConnections,
      culture,
      autoSearch,
      searchReq,
      tripType,
      lastSearchedCriteria,
      selectedCurrencyCode,
      countryCode,
    } as SearchDataModel
  }
);

export const selectAppId = createSelector(
  selectAppFeatureState,
  (state) => state.appId,
);

export const selectDisruptionBanners = createSelector(
  selectAppFeatureState,
  (state) => state.disruptionBanners,
);

export const selectDisruptionBannersClosed = createSelector(
  selectAppFeatureState,
  (state) => state.disruptionBannersClosed,
);

export const selectActiveDisruptionBanners = createSelector(
  selectDisruptionBanners,
  selectDisruptionBannersClosed,
  (disruptionBanners, disruptionBannersClosed) =>
    disruptionBanners?.filter(
      (banner) => !disruptionBannersClosed.includes(banner.externalId),
    )
);

export const selectCloudflareMeta = createSelector(
  selectAppFeatureState,
  (state) => state.cloudflareMeta,
);

export const selectCloudflareMetaCountry = createSelector(
  selectAppFeatureState,
  (state) => state.cloudflareMeta?.country,
);

export const selectDynamicPagesConfig = createSelector(
  selectAppFeatureState,
  (state) => state.dynamicPagesConfig,
);

export const selectIsDynamicPagesConfigLoaded = createSelector(
  selectAppFeatureState,
  (state) => state.isDynamicPagesConfigLoaded,
);
